import React, { useEffect, useState } from "react";
import "../styles/AltinSlider.css";
import { useWebSocket } from "../hooks/useWebSocket";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { FaCoins, FaDollarSign, FaExchangeAlt, FaUserCircle, FaShieldAlt, FaWifi, FaChartLine, FaMobileAlt, FaClock, FaWallet } from "react-icons/fa";
import AmblemLight from "../assets/images/amblem_light.png";
import AmblemLight2 from "../assets/images/amblemlight2.png";

const SUMMARY_CODES = ["HAS", "ONS", "GUMTRL"];

const AltinSlider: React.FC = () => {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const { data: prices, connectionStatus } = useWebSocket("wss://sslsocket.aifanet.com/5.250.255.84/12254");
  const logoSource = theme === 'light' ? AmblemLight : AmblemLight2;

  const menuItems = [
    { label: t('altinSlider.altin'), icon: <FaCoins color="#A7925B" size={32} /> },
    { label: t('altinSlider.doviz'), icon: <FaDollarSign color="#3b82f6" size={32} /> },
    { label: t('altinSlider.ceviri'), icon: <FaExchangeAlt color="#22c55e" size={32} /> },
    { label: t('altinSlider.portfoy'), icon: <FaUserCircle color="#a855f7" size={32} /> },
  ];

  const leftInfos = [
    {
      icon: <FaShieldAlt color="#A7925B" size={28} />, title: t('altinSlider.guvenli'), desc: t('altinSlider.guvenliDesc')
    },
    {
      icon: <FaWifi color="#3b82f6" size={28} />, title: t('altinSlider.erisilebilirlik'), desc: t('altinSlider.erisilebilirlikDesc')
    },
    {
      icon: <FaChartLine color="#22c55e" size={28} />, title: t('altinSlider.karlilik'), desc: t('altinSlider.karlilikDesc')
    },
  ];
  const rightInfos = [
    {
      icon: <FaMobileAlt color="#A7925B" size={28} />, title: t('altinSlider.mobilUygulama'), desc: t('altinSlider.mobilUygulamaDesc')
    },
    {
      icon: <FaClock color="#3b82f6" size={28} />, title: t('altinSlider.zaman'), desc: t('altinSlider.zamanDesc')
    },
    {
      icon: <FaWallet color="#a855f7" size={28} />, title: t('altinSlider.portfoyTitle'), desc: t('altinSlider.portfoyDesc')
    },
  ];

  // Önceki fiyatları saklamak için state
  const [prevPrices, setPrevPrices] = useState<{ [code: string]: number }>({});
  // Fiyat yönlerini saklamak için state
  const [priceDirections, setPriceDirections] = useState<{ [code: string]: "up" | "down" | null }>({});

  // Fiyatlar değiştiğinde yönleri hesapla
  useEffect(() => {
    const newDirections: { [code: string]: "up" | "down" | null } = {};
    const newPrevPrices: { [code: string]: number } = { ...prevPrices };
    prices.forEach((row) => {
      const prev = prevPrices[row.currencyPair];
      if (prev === undefined) {
        newDirections[row.currencyPair] = null; // İlk veri, yön yok
      } else if (row.sellRate > prev) {
        newDirections[row.currencyPair] = "up";
      } else if (row.sellRate < prev) {
        newDirections[row.currencyPair] = "down";
      } else {
        newDirections[row.currencyPair] = null;
      }
      newPrevPrices[row.currencyPair] = row.sellRate;
    });
    setPriceDirections(newDirections);
    setPrevPrices(newPrevPrices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices]);

  // Özet kutuları için ilgili kodları bul
  const summaryData = SUMMARY_CODES.map((code) => {
    const item = prices.find((p) => p.currencyPair === code);
    return {
      code,
      value: item ? item.sellRate.toLocaleString("tr-TR", { minimumFractionDigits: 4 }) : "-",
      change: "-", // Değişim oranı WebSocket verisinde yoksa boş bırak
    };
  });

  // Tablo verisi: Sadece alış/satış oranı olanlar
  const tableData = prices.map((row) => ({
    code: row.currencyPair,
    bid: row.buyRate !== undefined ? row.buyRate.toLocaleString("tr-TR", { minimumFractionDigits: 1 }) : "-",
    ask: row.sellRate !== undefined ? row.sellRate.toLocaleString("tr-TR", { minimumFractionDigits: 1 }) : "-",
    up: priceDirections[row.currencyPair] === "up",
    down: priceDirections[row.currencyPair] === "down",
    change: "-", // Değişim oranı yoksa boş bırak
    time: new Date().toLocaleTimeString("tr-TR"),
  }));

  // Sadece ilk 10 tanesini göster
  const visibleRows = tableData.slice(0, 10);
  const hasMoreRows = tableData.length > 10;

  return (
    <div className="altin-slider-main-flex">
      {/* Sol kutu */}
      <div className="altin-slider-sidebox altin-slider-sidebox-left">
        {leftInfos.map((info) => (
          <div className="altin-slider-infobox" key={info.title}>
            <div className="altin-slider-infoicon">{info.icon}</div>
            <div>
              <div className="altin-slider-infotitle">{info.title}</div>
              <div className="altin-slider-infodesc">{info.desc}</div>
            </div>
          </div>
        ))}
      </div>
      {/* Telefon mockup */}
      <div className="altin-slider-centerbox">
        <div className="altin-slider-phone-mockup">
          {/* Üstte logo ve özet fiyat kutuları */}
          <div className="slider-header-row">
            <div className="slider-logo-group" style={{ justifyContent: 'center', width: '100%' }}>
              <img src={logoSource} alt="HG Amblem" className="slider-logo-image" />
            </div>
          </div>
          <div className="slider-summary-row">
            {summaryData.map((item) => (
              <div className="slider-summary-box" key={item.code}>
                <div className="slider-summary-code">{item.code}</div>
                <div className="slider-summary-value">{item.value}</div>
                <div className="slider-summary-change">{item.change}</div>
              </div>
            ))}
          </div>
          {/* Tablo */}
          <div className="slider-table-wrapper" style={{ maxHeight: '370px', overflowY: hasMoreRows ? 'auto' : 'visible' }}>
            <table className="slider-table">
              <thead>
                <tr>
                  <th>{t('altinSlider.birim')}</th>
                  <th>{t('altinSlider.alis')}</th>
                  <th>{t('altinSlider.satis')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.code} className={row.up ? "slider-row-up" : row.down ? "slider-row-down" : ""}>
                    <td>
                      <div className="slider-table-code">{row.code}</div>
                      <div className="slider-table-time">{row.time}</div>
                    </td>
                    <td className="slider-table-bid">{row.bid}</td>
                    <td className="slider-table-ask">{row.ask}</td>
                    <td>
                      {row.up && <span className="slider-arrow-up">▲</span>}
                      {row.down && <span className="slider-arrow-down">▼</span>}
                      <span className="slider-table-change">{row.change}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Bilgi satırı */}
          <div className="slider-info-row">{t('altinSlider.fiyatlarBilgi')} ({connectionStatus})</div>
          {/* Menü */}
          <div className="slider-menu-row">
            {menuItems.map((item) => (
              <div className="slider-menu-item" key={item.label}>
                {item.icon}
                <div className="slider-menu-label">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Sağ kutu */}
      <div className="altin-slider-sidebox altin-slider-sidebox-right">
        {rightInfos.map((info) => (
          <div className="altin-slider-infobox" key={info.title}>
            <div className="altin-slider-infoicon">{info.icon}</div>
            <div>
              <div className="altin-slider-infotitle">{info.title}</div>
              <div className="altin-slider-infodesc">{info.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AltinSlider; 